import React, { useEffect, useState } from "react";
import "../../App.css";
import {
  Input,
  Form,
  Typography,
  message,
  Spin,
  Card,
  Statistic,
  Switch,
  Button,
  Modal,
  Dropdown,
  Menu,
  Table,
  Tag,
  Tooltip,
  Avatar,
  Space,
  Row,
  Col,
  Segmented
} from "antd";
import { EditOutlined, DeleteOutlined, EllipsisOutlined, UserOutlined, EyeOutlined, KeyOutlined } from '@ant-design/icons';
import EmpleadoComponent from "./Empleado";
import EmpleadoDetalleModal from "./EmpleadoDetalleModal";
import EditarEmpleadoModal from "./EditarEmpleadoModal";
import CambiarPasswordModal from "./CambiarPasswordModal";
import auditedServices from "../../servicios/empleados/serviciosAudit";
const { getEmpleadoDetalle, actualizarEmpleado, cambiarPassword } = auditedServices;
import { useAuth } from "../Context";
import FiltroSucursal from "../sucursales/FiltroSucursal";
import { useAuditoria } from "../../hooks/useAuditoria";
// Importamos el nuevo contexto de auditoría de Firestore
import { useFirestoreAudit } from "../../hooks/FirestoreAuditContext";
import { QuerySnapshot, DocumentData } from "firebase/firestore";
import { Empleado as EmpleadoType, EmpleadoInfo, ApiResponse } from "../../types";

const { Title } = Typography; 

const BuscarEmpleadoConAuditoria = () => {
  const [searchText, setSearchText] = useState("");
  const [employees, setEmployees] = useState<EmpleadoType[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [mostrarInactivos, setMostrarInactivos] = useState<boolean>(false);
  const [vista, setVista] = useState<'tabla' | 'tarjetas'>('tabla');
  const [modalDetalleVisible, setModalDetalleVisible] = useState<boolean>(false);
  const [modalEditarVisible, setModalEditarVisible] = useState<boolean>(false);
  const [empleadoDetalle, setEmpleadoDetalle] = useState<EmpleadoType | null>(null);
  const [detalleLoading, setDetalleLoading] = useState<boolean>(false);
  const [empleadoInfo, setEmpleadoInfo] = useState<EmpleadoInfo>({
    total: 0,
    activos: 0,
    inactivos: 0,
    hombres: 0,
    mujeres: 0,
  });
  const { auditar } = useAuditoria();
  
  // Accedemos al contexto de auditoría de Firestore
  const { onSnapshot, updateDoc, getDoc } = useFirestoreAudit();

  // Estados y handlers para el flujo de cambio de contraseña
  const [modalPasswordVisible, setModalPasswordVisible] = useState<boolean>(false);
  const [empleadoPassword, setEmpleadoPassword] = useState<EmpleadoType | null>(null);
  const [passwordLoading, setPasswordLoading] = useState<boolean>(false);

  const { sucursal, token, isAdmin, role } = useAuth();
  const [sucursalFiltro, setSucursalFiltro] = useState(sucursal || "");

  const handleAbrirModalPassword = (emp: EmpleadoType): void => {
    setEmpleadoPassword(emp);
    setModalPasswordVisible(true);
  };

  const handleCerrarModalPassword = (): void => {
    setModalPasswordVisible(false);
    setEmpleadoPassword(null);
  };

  const handleCambiarPassword = async (nuevaPassword: string): Promise<void> => {
    setPasswordLoading(true);
    try {
      if (!empleadoPassword || !token) {
        message.error("Datos incompletos para actualizar contraseña");
        setPasswordLoading(false);
        return;
      }
      
      const res = await cambiarPassword(token, empleadoPassword.id, nuevaPassword);
      if (res.success) {
        message.success("Contraseña actualizada correctamente");
        // Registrar en auditoría el cambio de contraseña
        auditar('cambiar_password', 'empleados', {
          empleado_id: empleadoPassword.id,
          nombre_empleado: `${empleadoPassword.nombre || ''} ${empleadoPassword.ap_paterno || ''} ${empleadoPassword.ap_materno || ''}`.trim()
        }).catch(error => {
          console.error("Error al registrar auditoría de cambio de contraseña:", error);
        });
        handleCerrarModalPassword();
      } else {
        message.error(res.message || "No se pudo actualizar la contraseña");
      }
    } catch (err) {
      message.error("Error al actualizar la contraseña");
    }
    setPasswordLoading(false);
  };

  useEffect(() => {
    if (!sucursalFiltro) return;
    
    setLoading(true);
    
    // Nueva estructura: empleado en raíz con query por sucursal
    const unsubscribe = onSnapshot(
      `/empleado`, 
      'Empleados',
      (querySnapshot: QuerySnapshot<DocumentData>) => {
        const employeesData = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          } as EmpleadoType))
          .filter(emp => emp.sucursal === sucursalFiltro); // Filtrar por sucursal

        // Calculamos estadísticas
        const total = employeesData.length;
        const activos = employeesData.filter(emp => emp.estado !== 'Inactivo').length;
        const inactivos = total - activos;
        const hombres = employeesData.filter(emp => emp.sexo === 'Masculino').length;
        const mujeres = employeesData.filter(emp => emp.sexo === 'Femenino').length;

        setEmpleadoInfo({ 
          total, 
          activos, 
          inactivos, 
          hombres, 
          mujeres 
        });
        
        setEmployees(employeesData);
        setLoading(false);
      }
    );
    
    // Limpieza al desmontar
    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, [sucursalFiltro, onSnapshot]);

  const buscarEmpleados = (e: React.ChangeEvent<HTMLInputElement>): void => {
    setSearchText(e.target.value);
  };

  const handleCambioEstado = async (empleado: EmpleadoType, nuevoEstado: string): Promise<void> => {
    try {
      // Nueva estructura: empleado en raíz
      await updateDoc(
        `/empleado`, 
        empleado.id, 
        { estado: nuevoEstado }, 
        'Empleados'
      );
      
      message.success(`El estado del empleado ha sido actualizado a ${nuevoEstado}`);
    } catch (error) {
      console.error("Error al cambiar estado:", error);
      message.error("Error al actualizar el estado del empleado");
    }
  };

  const handleGetEmpleadoDetalle = async (id: string): Promise<void> => {
    setDetalleLoading(true);
    try {
      // Nueva estructura: empleado en raíz
      const docSnap = await getDoc(
        `/empleado`,
        id,
        'Empleados'
      );
      
      if (docSnap.exists()) {
        setEmpleadoDetalle({
          id: docSnap.id,
          ...docSnap.data()
        } as EmpleadoType);
      } else {
        message.error("No se encontró información del empleado");
      }
    } catch (error) {
      console.error("Error al obtener detalle:", error);
      message.error("Error al cargar los detalles del empleado");
    }
    setDetalleLoading(false);
    setModalDetalleVisible(true);
  };

  const handleEditarEmpleado = (empleado: EmpleadoType): void => {
    setEmpleadoDetalle(empleado);
    setModalEditarVisible(true);
  };

  const handleCerrarModal = (): void => {
    setModalDetalleVisible(false);
    setEmpleadoDetalle(null);
  };

  const handleCerrarModalEditar = (): void => {
    setModalEditarVisible(false);
  };

  const handleGuardarEdicion = async (values: any): Promise<void> => {
    try {
      if (!empleadoDetalle || !token) {
        message.error("Datos incompletos para actualizar empleado");
        return;
      }
      
      // Combinamos el id del empleado con los valores actualizados
      const dataToUpdate = {
        ...values,
        id: empleadoDetalle.id
      };
      
      const resultado = await actualizarEmpleado(token, dataToUpdate);
      if (resultado.success) {
        message.success("Empleado actualizado correctamente");
        handleCerrarModalEditar();
      } else {
        message.error(resultado.message || "Error al actualizar empleado");
      }
    } catch (error) {
      console.error("Error al actualizar:", error);
      message.error("Error al procesar la actualización");
    }
  };

  const filteredEmployees = employees.filter((emp) => {
    // Filtro por texto
    const searchFilter = searchText.toLowerCase();
    const nombreCompleto = `${emp.nombre || ''} ${emp.ap_paterno || ''} ${emp.ap_materno || ''}`.toLowerCase();
    const textMatch = nombreCompleto.includes(searchFilter) || 
                      (emp.email || '').toLowerCase().includes(searchFilter) ||
                      (emp.puesto || '').toLowerCase().includes(searchFilter);
                      
    // Filtro por estado
    const estadoMatch = mostrarInactivos ? true : emp.estado !== "Inactivo";
    
    return textMatch && estadoMatch;
  });

  const cardStyle = {
    width: 300,
    marginBottom: 16,
    boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
  };

  const columns = [
    {
      title: 'Nombre',
      dataIndex: 'nombre',
      key: 'nombre',
      render: (_: any, record: EmpleadoType) => (
        <Space>
          <Avatar
            icon={<UserOutlined />}
            src={record.foto}
            style={{ backgroundColor: !record.foto ? '#1890ff' : undefined }}
          />
          <span>{`${record.nombre || ''} ${record.ap_paterno || ''} ${record.ap_materno || ''}`}</span>
        </Space>
      ),
      sorter: (a: EmpleadoType, b: EmpleadoType) => {
        const nameA = `${a.nombre || ''} ${a.ap_paterno || ''}`.toLowerCase();
        const nameB = `${b.nombre || ''} ${b.ap_paterno || ''}`.toLowerCase();
        return nameA.localeCompare(nameB);
      }
    },
    {
      title: 'Correo',
      dataIndex: 'email',
      key: 'email',
    },
    {
      title: 'Puesto',
      dataIndex: 'puesto',
      key: 'puesto',
    },
    {
      title: 'Estado',
      dataIndex: 'estado',
      key: 'estado',
      render: (estado: string) => {
        let color = 'green';
        if (estado === 'Inactivo') color = 'red';
        else if (estado === 'Suspendido') color = 'orange';
        return <Tag color={color}>{estado || 'Activo'}</Tag>;
      },
      sorter: (a: EmpleadoType, b: EmpleadoType) => {
        const stateA = a.estado || 'Activo';
        const stateB = b.estado || 'Activo';
        return stateA.localeCompare(stateB);
      }
    },
    {
      title: 'Acciones',
      key: 'actions',
      render: (_: any, record: EmpleadoType) => (
        <Space size="small">
          <Tooltip title="Ver detalles">
            <Button 
              icon={<EyeOutlined />} 
              onClick={() => handleGetEmpleadoDetalle(record.id)} 
            />
          </Tooltip>
          <Tooltip title="Editar">
            <Button 
              icon={<EditOutlined />} 
              onClick={() => handleEditarEmpleado(record)} 
            />
          </Tooltip>
          <Tooltip title="Cambiar contraseña">
            <Button 
              icon={<KeyOutlined />}
              onClick={() => handleAbrirModalPassword(record)}
            />
          </Tooltip>
          <Dropdown overlay={
            <Menu>
              <Menu.Item 
                key="1" 
                onClick={() => handleCambioEstado(record, record.estado === 'Inactivo' ? 'Activo' : 'Inactivo')}
              >
                {record.estado === 'Inactivo' ? 'Activar' : 'Desactivar'}
              </Menu.Item>
              <Menu.Item 
                key="2" 
                onClick={() => handleCambioEstado(record, 'Suspendido')}
              >
                Suspender
              </Menu.Item>
            </Menu>
          } trigger={['click']}>
            <Button icon={<EllipsisOutlined />} />
          </Dropdown>
        </Space>
      )
    }
  ];

  return (
    <>
      <div style={{ padding: 24, maxWidth: '100%' }}>
        <Row gutter={[16, 16]} justify="space-between" align="middle">
          <Col>
            <Title level={2}>Empleados</Title>
          </Col>
          <Col>
            {isAdmin && (
              <FiltroSucursal 
                onSucursalChange={(sucursal: string) => setSucursalFiltro(sucursal)} 
                value={sucursalFiltro}
              />
            )}
          </Col>
        </Row>

        <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Card>
              <Statistic title="Total" value={empleadoInfo.total} />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Card>
              <Statistic title="Activos" value={empleadoInfo.activos} valueStyle={{ color: '#3f8600' }} />
            </Card>
          </Col>
          <Col xs={24} sm={8} md={6} lg={4} xl={4}>
            <Card>
              <Statistic title="Inactivos" value={empleadoInfo.inactivos} valueStyle={{ color: '#cf1322' }} />
            </Card>
          </Col>
        </Row>

        <Row gutter={16} align="middle" style={{ marginBottom: 16 }}>
          <Col flex="auto">
            <Input
              placeholder="Buscar por nombre, correo o puesto"
              size="large"
              onChange={buscarEmpleados}
              style={{ width: '100%' }}
            />
          </Col>
          <Col>
            <Space>
              <span>Mostrar inactivos</span>
              <Switch checked={mostrarInactivos} onChange={setMostrarInactivos} />
            </Space>
          </Col>
          <Col>
            <Segmented
              options={[
                { value: 'tabla', icon: <i className="bx bx-table"></i> },
                { value: 'tarjetas', icon: <i className="bx bx-grid-alt"></i> }
              ]}
              value={vista}
              onChange={setVista}
            />
          </Col>
        </Row>

        {loading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin size="large" />
          </div>
        ) : vista === 'tabla' ? (
          <Table 
            dataSource={filteredEmployees} 
            columns={columns}
            rowKey="id"
            pagination={{ pageSize: 10 }}
            locale={{ emptyText: "No hay empleados que coincidan con la búsqueda" }}
          />
        ) : (
          <Row gutter={[16, 16]}>
            {filteredEmployees.length > 0 ? (
              filteredEmployees.map(empleado => (
                <Col key={empleado.id} xs={24} sm={12} md={8} lg={8} xl={6}>
                  <EmpleadoComponent 
                    employee={empleado} 
                    onVerDetalle={handleGetEmpleadoDetalle}
                    onEditar={handleEditarEmpleado}
                    onCambiarPassword={handleAbrirModalPassword}
                  />
                </Col>
              ))
            ) : (
              <Col span={24} style={{ textAlign: 'center', padding: 24 }}>
                <p>No hay empleados que coincidan con la búsqueda</p>
              </Col>
            )}
          </Row>
        )}
      </div>

      <EmpleadoDetalleModal
        visible={modalDetalleVisible}
        empleado={empleadoDetalle as EmpleadoType}
        loading={detalleLoading}
        onClose={handleCerrarModal}
      />

      <EditarEmpleadoModal
        visible={modalEditarVisible}
        empleado={empleadoDetalle}
        loading={false}
        sucursal={sucursalFiltro || ""}
        onClose={handleCerrarModalEditar}
        onSave={handleGuardarEdicion}
      />

      <CambiarPasswordModal 
        visible={modalPasswordVisible}
        empleado={empleadoPassword}
        loading={passwordLoading}
        onCancel={handleCerrarModalPassword}
        onOk={handleCambiarPassword}
      />
    </>
  );
};

export default BuscarEmpleadoConAuditoria;
